import { Grid, Typography } from "@material-ui/core";
import React from "react";
import moment from "moment";
import {
    buildingType,
    mitigationSystem,
    reportResultColor,
    structureType,
    testPurpose,
} from "../../../constants/values";
import { convertToPicocuries } from "../../../utils/convertToPicocuries";
import {
    calculateEpaAverage,
    reformatRadonAvgData,
} from "../../../utils/calculateEpaAverage";
import { getCompanyInfo } from "../../../models/services/authService";
import ReportPageHeader from "./ReportPageHeader";
import {
    BUILDING_TYPE_KEY,
    MEASUREMENT_UNIT,
    MITIGATION_SYSTEM_KEY,
    REPORT_RESULT_COLOR,
    REPORT_RESULT_SU,
    ROOM_TYPE_KEY,
    STRUCTURE_TYPE_KEY,
    TEST_FLOOR_KEY,
    UNIT_CONVERSION_SCALE,
} from "../../../constants/values";

const styles = {
    sectionTitle: {
        color: "black",
        fontSize: "11px",
        fontWeight: 700,
        fontStyle: "italic",
        textAlign: "left",
        marginBottom: 4,
    },
    sectionDivider: {
        height: "4px",
        background: "#575195",
        marginBottom: 5,
        marginTop: 5,
    },
    labelCol: {
        color: "#333",
        fontSize: "10px",
        fontWeight: 400,
        textAlign: "left",
        lineHeight: "1.7",
    },
    valueCol: {
        color: "#000",
        fontSize: "10px",
        fontWeight: 500,
        textAlign: "left",
        lineHeight: "1.7",
    },
};

/** Two-column row: label on left, value on right */
const InfoRow = ({ label, value }) => {
    // Prevent React crash from rendering empty objects {} that APIs sometimes return
    const isPlainObject = typeof value === "object" && value !== null && !React.isValidElement(value) && !Array.isArray(value);
    const safeValue = isPlainObject ? (Object.keys(value).length === 0 ? "" : JSON.stringify(value)) : value;

    return (
        <Grid container style={{ paddingLeft: 35, paddingRight: 20 }}>
            <Grid item xs={6}>
                <Typography style={styles.labelCol}>{label}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography style={styles.valueCol}>{safeValue ?? ""}</Typography>
            </Grid>
        </Grid>
    );
};

const SectionHeader = ({ title }) => (
    <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 5 }}>
        <div style={styles.sectionDivider} />
        <Typography style={styles.sectionTitle}>{title}</Typography>
    </div>
);

/**
 * PageMain for 48h report.
 * Supports both MU-web field names (camelCase) and EQWebDashboard field names (snake_case).
 */
const PageMain = ({ theme, reportDetails, reportChartRadonValue }) => {
    const [companyInfo, setCompanyInfo] = React.useState(undefined);

    React.useEffect(() => {
        if (!reportDetails) return;
        const fetchCompanyInfo = async () => {
            try {
                const targetEmail = reportDetails.email ?? reportDetails.test_by?.ownerDisplay ?? "";
                if (!targetEmail) return;

                const trimmedEmail = targetEmail.trim().toLowerCase();
                const res = await getCompanyInfo(trimmedEmail);
                if (res && res.data) {
                    setCompanyInfo(res.data);
                } else {
                    setCompanyInfo(null);
                }
            } catch (err) {
                console.error("Failed to load company info", err);
                setCompanyInfo(null);
            }
        };
        fetchCompanyInfo();
    }, [reportDetails]);

    if (!reportDetails) return null;

    /* ---------- helper fns ---------- */
    const reformatRadonData = (data, unit, point) => {
        if (data === "" || data === undefined || data === null || data === -1)
            return "";
        if (unit === 0) return (data / 37).toFixed(point) + " pCi/L";
        return data.toFixed(0) + " Bq/m³";
    };

    // Support both camelCase (MU-web API) and snake_case (EQWebDashboard API)
    const measurementUnitVal =
        reportDetails.measurementUnit ?? reportDetails.measurement_unit ?? 0;
    const radonAvg =
        reportDetails.radonAvg ?? reportDetails.avg_radon;
    const serialNumber =
        reportDetails.serialNumber ?? reportDetails.serial_number;
    let calibrationExpired =
        reportDetails.calibrationExpired ?? reportDetails.calibration_date;
    if (calibrationExpired) {
        const m = moment(calibrationExpired);
        if (m.isValid()) {
            calibrationExpired = m.add(1, "years").format("MM/DD/YYYY");
        }
    }

    const propertyAddress = () => {
        const a = reportDetails.device_location;
        if (!a) return "";
        return [a.address1 ?? a.street, a.address2, a.city, a.state, a.zipcode]
            .filter(Boolean)
            .join(" ");
    };

    /* ---------- Test Result ---------- */
    let calculatedTestResult = reportDetails.testResult;
    let testResultLabel = "";
    let testResultColor = "black";
    if (
        measurementUnitVal === 0 &&
        radonAvg !== undefined &&
        radonAvg !== null &&
        radonAvg !== -1
    ) {
        const pCiValue = convertToPicocuries(radonAvg);
        if (pCiValue > 4.0) {
            testResultLabel = ">EPA Guidelines";
            testResultColor = reportResultColor[0] ?? "black";
        } else if (pCiValue === 4.0) {
            testResultLabel = "≤EPA Guidelines";
            testResultColor = reportResultColor[1] ?? "black";
        } else {
            testResultLabel = "<EPA Guidelines";
            testResultColor = reportResultColor[1] ?? "black";
        }
    } else {
        testResultLabel =
            calculatedTestResult === -1
                ? ""
                : calculatedTestResult === 0
                    ? "≥EPA Guidelines"
                    : "<EPA Guidelines";
        testResultColor =
            calculatedTestResult === -1
                ? "black"
                : reportResultColor[calculatedTestResult] ?? "black";
    }

    const overallAvg = reformatRadonData(radonAvg, measurementUnitVal, 1);
    const epaAvg = reformatRadonAvgData(
        calculateEpaAverage(reportChartRadonValue),
        measurementUnitVal,
        1
    );

    // Client info (support both structures)
    const clientName = reportDetails.client ? reportDetails.client.client_name : reportDetails.client_name;
    const clientEmail = reportDetails.client ? reportDetails.client.client_display : reportDetails.client_display ? reportDetails.client_display : reportDetails.client_email;
    const clientPhone = reportDetails.client ? reportDetails.client.client_phone : reportDetails.client_phone;
    const locationId = reportDetails.device_name;

    // Placement info (EQWebDashboard uses device_placement)
    const bldType =
        reportDetails.buildingType ?? reportDetails.device_placement?.bld_type;
    const strType =
        reportDetails.structureType ?? reportDetails.device_placement?.str_type;
    const mitigation =
        reportDetails.mitigationSystem ??
        reportDetails.device_placement?.mitigation_installed;
    const yearBuilt =
        reportDetails.yearBuilt ?? reportDetails.device_placement?.year_built;

    return (
        <div style={{ height: "750px", overflow: "hidden" }}>
            {/* ===== Shared Header ===== */}
            <ReportPageHeader reportDetails={reportDetails} />

            {/* ===== Title ===== */}
            <Typography
                style={{
                    textAlign: "center",
                    fontWeight: 900,
                    color: "black",
                    fontSize: "24px",
                    marginTop: 5,
                    marginBottom: 0,
                }}
            >
                Radon Report
            </Typography>

            <SectionHeader title="Tested By" />
            <InfoRow
                label="Company Name"
                value={companyInfo === undefined ? "" : (companyInfo?.companyName || "")}
            />
            <InfoRow
                label="Address"
                value={companyInfo === undefined ? "" : (
                    <>
                        {companyInfo?.address?.street && (
                            <>
                                {companyInfo.address.street}
                                {companyInfo.address.detail ? " " + companyInfo.address.detail : ""}
                                {" "}
                            </>
                        )}
                        {companyInfo?.address && [companyInfo.address.city, companyInfo.address.state, companyInfo.address.zipcode].filter(Boolean).join(" ")}
                    </>
                )}
            />

            {/* ===== PROPERTY INFORMATION ===== */}
            <SectionHeader title="Property Information" />
            <InfoRow label="Property Name" value={locationId} />
            <InfoRow label="Contact Person" value={clientName} />
            <InfoRow label="Address" value={propertyAddress()} />
            <InfoRow label="Phone" value={clientPhone} />
            <InfoRow label="Email" value={clientEmail} />
            <div style={{ height: 4 }} />
            <InfoRow label="Building Type" value={reportDetails.device_placement ? BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type]
                ? BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type] : "N/A"
                : "N/A"} />
            <InfoRow label="Structure Type" value={reportDetails.device_placement ? STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type]
                ? STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type] : "N/A"
                : "N/A"} />
            <InfoRow label="Mitigation System" value={reportDetails.device_placement ? MITIGATION_SYSTEM_KEY[
                reportDetails.device_placement.mitigation_installed
            ]
                ? MITIGATION_SYSTEM_KEY[
                reportDetails.device_placement.mitigation_installed
                ] : "N/A"
                : "N/A"} />
            <InfoRow label="Year Built" value={reportDetails.device_placement ? reportDetails.device_placement.year_built
                ? reportDetails.device_placement.year_built : "N/A"
                : "N/A"} />

            {/* ===== TEST RESULT ===== */}
            <SectionHeader title="Test Result:" />
            <Grid container style={{ paddingLeft: 35, paddingRight: 20, marginTop: 4 }}>
                {/* Left: big result text */}
                <Grid item xs={6}>
                    <Typography
                        style={{
                            color: testResultColor,
                            fontSize: "18px",
                            fontWeight: 1000,
                            textAlign: "left",
                        }}
                    >
                        {testResultLabel}
                    </Typography>
                    <Typography
                        style={{
                            color: "#696F8C",
                            fontSize: "8px",
                            fontWeight: 400,
                            marginTop: 6,
                            textAlign: "left",
                            whiteSpace: "pre-line",
                        }}
                    >
                        EPA recommends mitigation{"\n"}if the radon average is 4pCi/L or more
                    </Typography>
                </Grid>
                {/* Right: averages */}
                <Grid item xs={6}>
                    <Grid container alignItems="center" style={{ marginBottom: 0 }}>
                        <Grid item xs={4}>
                            <Typography style={{ color: "#000", fontWeight: 400, fontSize: "11px", textAlign: "left" }}>
                                Overall Average
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography style={{ color: "black", fontSize: "18px", fontWeight: 700 }}>
                                {overallAvg}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container alignItems="center">
                        <Grid item xs={4}>
                            <Typography style={{ color: "#000", fontWeight: 400, fontSize: "11px", textAlign: "left" }}>
                                EPA Average*
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography style={{ color: "black", fontSize: "18px", fontWeight: 700 }}>
                                {epaAvg}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Typography style={{ color: "#696F8C", fontWeight: 400, fontSize: "8px", marginTop: 4, textAlign: "left" }}>
                        *The EPA protocol average discards the first four hours of the
                        deployment period. The official number to go with is the EPA
                        protocol average.
                    </Typography>
                </Grid>
            </Grid>

            {/* ===== DEVICE INFORMATION ===== */}
            <SectionHeader title="Device Information" />
            <InfoRow label="Device Name" value="EcoGuard" />
            <InfoRow label="Model Number" value="EQ200EG" />
            <InfoRow label="Serial Number" value={serialNumber} />
            <InfoRow label="Manufacturer" value="FTLab" />
            <InfoRow label="Calibration Exp. Date" value={calibrationExpired ?? ""} />
            <InfoRow label="Device License" value="NRPP 6346" />
            <InfoRow label="MDC*" value="0.2 pCi/L" />
            <Typography
                style={{
                    color: "#696F8C",
                    fontSize: "8px",
                    paddingLeft: 35,
                    marginTop: 2,
                    textAlign: "left",
                }}
            >
                *Minimum Detectable Concentration
            </Typography>
        </div>
    );
};

export default PageMain;
